import React, { useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useHover } from '@hooks/useHover';
import theme from '@themes/theme';

export type TaskFilesFilterPopupProps = {
  onSelectMedia?: (selected: boolean) => void;
  onSelectDocument?: (selected: boolean) => void;
  onSearchChange?: (searchText: string) => void;
  onClearAll?: () => void;
  mediaSelected?: boolean;
  documentSelected?: boolean;
  searchValue?: string;
};

export const PopupTaskFilesFilter: React.FC<TaskFilesFilterPopupProps> = ({
  onSelectMedia,
  onSelectDocument,
  onSearchChange,
  onClearAll,
  mediaSelected = false,
  documentSelected = false,
  searchValue = '',
}) => {
  const [mouseOverMedia, setMouseOverMedia] = useState(false);
  const [mouseOverDocument, setMouseOverDocument] = useState(false);
  const { isHovered: isClearAllHovered, hoverProps: clearAllHoverProps } =
    useHover();
  const [search, setSearch] = useState<string>(searchValue);

  // Sync internal search state with searchValue prop when it changes
  useEffect(() => {
    setSearch(searchValue);
  }, [searchValue]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const enabled = search.length > 0 || mediaSelected || documentSelected;

  return (
    <ShadowBox
      width={300}
      flex={1}
      borderWidth={1}
      alignItems='flex-start'
      borderRadius='xs'
      backgroundColor='white'>
      {/* Search Input */}
      <Box
        marginHorizontal='m'
        marginTop='m'
        style={{
          width: 268,
          backgroundColor: 'grey01',
        }}
        justifyContent='center'
        flexDirection='row'
        alignItems='flex-start'>
        <SearchInput
          value={search}
          height={40}
          onTextChanged={handleSearchChange}
          placeHolder='Search uploader'
        />
      </Box>

      {/* Divider */}
      <Box
        style={{ width: '100%' }}
        height={1}
        backgroundColor='grey02'
        marginVertical='s'
      />

      {/* Media Checkbox */}
      <TouchableOpacity
        onPress={() => onSelectMedia?.(!mediaSelected)}
        activeOpacity={0.7}>
        <Box
          width={300}
          flexDirection='row'
          alignItems='center'
          paddingHorizontal='m'
          paddingVertical='s'
          style={{
            paddingVertical: 9,
            backgroundColor: mouseOverMedia
              ? theme.colors.grey01
              : theme.colors.white,
          }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseEnter={() => {
            setMouseOverMedia(true);
          }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseLeave={() => {
            setMouseOverMedia(false);
          }}>
          <Box marginRight='xs' justifyContent='center'>
            <CheckBoxButton checked={mediaSelected} />
          </Box>
          <Text variant='bodySecondary' color='textPrimary'>
            Media
          </Text>
        </Box>
      </TouchableOpacity>

      {/* Document Checkbox */}
      <TouchableOpacity
        onPress={() => onSelectDocument?.(!documentSelected)}
        activeOpacity={0.7}>
        <Box
          width={300}
          flexDirection='row'
          alignItems='center'
          paddingHorizontal='m'
          paddingVertical='s'
          style={{
            paddingVertical: 9,
            backgroundColor: mouseOverDocument
              ? theme.colors.grey01
              : theme.colors.white,
          }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseEnter={() => {
            setMouseOverDocument(true);
          }}
          // @ts-ignore - onMouseEnter/onMouseLeave are valid web props
          onMouseLeave={() => {
            setMouseOverDocument(false);
          }}>
          <Box marginRight='xs' justifyContent='center'>
            <CheckBoxButton checked={documentSelected} />
          </Box>
          <Text variant='bodySecondary' color='textPrimary'>
            Document
          </Text>
        </Box>
      </TouchableOpacity>

      {/* Divider */}
      <Box
        style={{ width: '100%' }}
        height={1}
        backgroundColor='grey02'
        marginTop='s'
      />

      {/* Clear All Button */}
      <TouchableOpacity
        disabled={search.length === 0 && !mediaSelected && !documentSelected}
        onPress={onClearAll}
        activeOpacity={0.7}
        style={{ flex: 1, width: '100%' }}>
        <Box
          alignItems='center'
          paddingVertical='m'
          backgroundColor={isClearAllHovered && enabled ? 'grey01' : 'white'}
          {...clearAllHoverProps}>
          <Text variant='bodySecondary' color={enabled ? 'grey05' : 'grey04'}>
            Clear All
          </Text>
        </Box>
      </TouchableOpacity>
    </ShadowBox>
  );
};
